/**
 * streamingUtils - Utilities for handling SSE streaming
 */
import { toast } from 'sonner';
import { auth } from '../../../utils/auth';
import i18n from '../../../i18n';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Get current language
 */
const getCurrentLang = () => {
  return i18n.language === 'en' ? 'en' : 'no';
};

/**
 * Process SSE streaming response
 * @param {Object} options - Streaming options
 * @param {string} options.threadId - Thread ID
 * @param {string} options.content - Message content
 * @param {Array} options.attachments - Attachments
 * @param {boolean} options.autoDraft - Whether to auto-draft
 * @param {string} options.existingDraftId - Existing draft ID
 * @param {string} options.lang - Language preference ('en' or 'no')
 * @param {Object} callbacks - Event callbacks
 */
export const streamChatMessage = async (options, callbacks) => {
  const {
    threadId,
    content,
    attachments = [],
    autoDraft = false,
    existingDraftId = null,
    lang = getCurrentLang()
  } = options;

  const {
    onContent,        // (content) => void - Regular chat content
    onDocStart,       // (intro) => void - Document generation started
    onDocContent,     // (content) => void - Document content chunk
    onDraftId,        // (draftId) => void - Draft created
    onChatSummary,    // (summary) => void - Chat summary for draft
    onMessageId,      // (messageId) => void - Final message ID
    onDone,           // (data) => void - Stream completed
    onError           // (error) => void - Error occurred
  } = callbacks;

  const token = auth.getToken();
  const streamUrl = `${API}/chat/messages/stream`;
  const attachmentData = attachments.map((a: any) => ({ id: a.id, filename: a.filename }));
  
  // Use native fetch (saved before rrweb loads)
  const nativeFetch = (window as any).__nativeFetch || fetch;
  
  try {
    const response = await nativeFetch(streamUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      credentials: 'include',
      body: JSON.stringify({
        thread_id: threadId,
        content,
        attachments: attachmentData,
        auto_draft: autoDraft,
        existing_draft_id: existingDraftId,
        lang
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      try {
        const errorData = JSON.parse(errorText);
        throw new Error(errorData.detail || `HTTP ${response.status}`);
      } catch {
        throw new Error(`Server error: ${response.status}`);
      }
    }

    if (!response.body) {
      throw new Error('Response body is null');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let finalMessageId = null;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      const text = decoder.decode(value, { stream: true });
      buffer += text;
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;

        try {
          const parsed = JSON.parse(line.slice(6));

          // Handle error
          if (parsed.error) {
            onError?.(new Error(parsed.error));
            return { success: false, error: parsed.error };
          }

          // Handle regular content
          if (parsed.content) {
            onContent?.(parsed.content);
          }

          // Handle document start
          if (parsed.doc_start) {
            onDocStart?.(parsed.intro);
          }

          // Handle document content
          if (parsed.doc_content) {
            onDocContent?.(parsed.doc_content);
          }

          // Handle draft ID
          if (parsed.draft_id) {
            onDraftId?.(parsed.draft_id);
          }

          // Handle chat summary
          if (parsed.chat_summary) {
            onChatSummary?.(parsed.chat_summary);
          }

          // Handle message ID
          if (parsed.message_id) {
            finalMessageId = parsed.message_id;
            onMessageId?.(parsed.message_id);
          }

          // Handle done signal
          if (parsed.done) {
            onDone?.({
              messageId: finalMessageId,
              draftId: parsed.draft_id,
              intro: parsed.intro,
              chatSummary: parsed.chat_summary
            });
          }
        } catch {
          // Ignore SSE parse errors
        }
      }
    }

    return { success: true, messageId: finalMessageId };
  } catch (error) {
    console.error('Streaming error:', error);
    onError?.(error);
    return { success: false, error: error.message };
  }
};

/**
 * Save a message to the database
 */
export const saveMessage = async (threadId, content, isDraft = false) => {
  try {
    const token = auth.getToken();
    await fetch(`${API}/chat/messages/save`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        thread_id: threadId,
        content,
        is_draft: isDraft
      })
    });
    return true;
  } catch (error) {
    console.error('Failed to save message:', error);
    return false;
  }
};

export default { streamChatMessage, saveMessage };
